import Chip from '@mui/material/Chip'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import React, { ReactNode, useMemo } from 'react'

type Props = {
  autoFocus?: boolean
  endAdornment?: ReactNode
  error?: boolean
  fullWidth?: boolean
  helperText?: ReactNode
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement | undefined>
  label?: string
  startAdornment?: ReactNode
  tags: string[]
  value: string
  variant?: 'filled' | 'outlined'
  formatTag?: (tag: string) => string
  onBlur?: () => void
  onChange: (value: string) => void
  onDelete: (index: number) => void
  onFocus?: () => void
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  onKeyPress?: React.KeyboardEventHandler<HTMLDivElement>
}

const InputTag: React.FC<Props> = ({
  endAdornment,
  error,
  inputRef,
  startAdornment,
  tags = [],
  value,
  formatTag,
  onChange,
  onDelete,
  ...restProps
}) => {
  const formattedTags = useMemo(() => {
    if (!formatTag) {
      return tags
    }

    return tags.map((tag) => formatTag(tag))
  }, [tags, formatTag])

  return (
    <div>
      <InputContainer>
        <InputWrapper
          variant="standard"
          data-testid="input-tag-text-field"
          error={error}
          inputRef={inputRef}
          multiline={true}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          slotProps={{
            input: {
              endAdornment,
              startAdornment,
              sx: {
                lineHeight: '1.8em',
                pb: '5px',
              },
            },
          }}
          {...restProps}
        />
      </InputContainer>
      <TagsContainer data-testid="input-tag-tags-container">
        {formattedTags.map((input, index) => (
          <Chip
            key={`${index}-${input}`}
            color="primary"
            variant="outlined"
            size="small"
            label={input}
            sx={{ mr: '5px' }}
            onDelete={() => onDelete(index)}
          />
        ))}
      </TagsContainer>
    </div>
  )
}

const InputContainer = styled('div')({
  display: 'flex',
})

const InputWrapper = styled(TextField)({
  height: 'auto',
  maxHeight: 'none',
  minHeight: '42px',
  whiteSpace: 'unset',
  flex: 1,
})

const TagsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  marginTop: '5px',
})

export default InputTag
